import { Injectable } from "@angular/core";
import {
  BfxBlotterActionEvent,
  BfxBaseBlotterContextMenuService,
  BfxLadderBlotterContextMenuEvent,
  BfxLadderBlotterContextMenuEventType,
} from "../bfx-base-blotter/bfx-base-blotter-contextmenu.service";
import {
  BfxBlotterContextMenuItem,
  BfxBlotterAction,
  OrderDataPromiseLookupService,
  SystemConfigPromiseService,
  TraderGroupPromiseLookupService,
  BfxBlotterOrdersContextMenuPromiseService,
  BfxBlotterOrdersContextMenuItem,
  BfxBlotterOrdersContextMenuState,
  ContextMenuItemState,
  ActiveUserProfilePromiseService,
  ActiveUser,
  Roles,
  SysFeatures,
} from "shared/cubiq-services";
import * as CubiqTS from "shared/cubiq-model-multi-asset";

export class BfxLadderBlotterOrdersContextMenuEvent extends BfxLadderBlotterContextMenuEvent<CubiqTS.OrderDataImpl> {}
import { LoggerService } from "@bfx/logger";
const logger = LoggerService.for(__dirname);

import { RowNode } from "ag-grid-community";
import { BfxBlotterOrdersActionSubject } from "./bfx-blotter-orders-action-subject.module";

@Injectable()
export class BfxBlotterOrdersContextMenuService extends BfxBaseBlotterContextMenuService<CubiqTS.OrderDataImpl> {
  public menuItems: Array<BfxBlotterOrdersContextMenuItem>;
  protected menuState = new BfxBlotterOrdersContextMenuState();
  protected contextmenuPromises: Array<PromiseLike<void>> = null;

  public readonly initialised: Promise<boolean>;

  constructor(
    protected orderDataService: OrderDataPromiseLookupService,
    protected systemConfigService: SystemConfigPromiseService,
    protected traderGroupService: TraderGroupPromiseLookupService,
    protected activeUserService: ActiveUserProfilePromiseService,
    protected contextMenuService: BfxBlotterOrdersContextMenuPromiseService,
    // note this is shared with Orders+Inbox(via subclass)+Ladder(via providers in component)
    actionSubject: BfxBlotterOrdersActionSubject,
  ) {
    super(actionSubject);

    this.menuState.canViewTicket = false;

    this.contextmenuPromises = [
      this.systemConfigService.hasRole(Roles.ORDER_FILL).then((value) => {
        logger.out.info(`BfxBlotterOrdersContextMenuService: ORDER_FILL ${value}`);
        this.menuState.canFillOrders = this.menuState.canQuickFillOrders = value;
      }),

      this.systemConfigService
        .getSystemConfig("system.features.slQuickFillEnabled")
        .then((value) => {
          logger.out.info(
            `BfxBlotterOrdersContextMenuService: ORDER_FILL - SlQuickFillEnabled ${value}`,
          );
          this.menuState.slQuickFillEnabled = value;
        }),

      this.systemConfigService.hasRole(Roles.ORDER_REACTIVATE).then((value) => {
        logger.out.info(`BfxBlotterOrdersContextMenuService: ORDER_REACTIVATE ${value}`);
        this.menuState.canReactivateOrders = value;
      }),

      this.systemConfigService.hasRole(Roles.ORDER_MODIFY).then((value) => {
        logger.out.info(`BfxBlotterOrdersContextMenuService: ORDER_MODIFY ${value}`);
        this.menuState.canModify = value;
      }),

      this.systemConfigService.hasRole(Roles.ORDER_MODIFY_TRIGGERED).then((value) => {
        logger.out.info(`BfxBlotterOrdersContextMenuService: ORDER_MODIFY_TRIGGERED ${value}`);
        this.menuState.canModifyTriggered = value;
      }),

      this.systemConfigService.hasRole(Roles.ORDER_UNRESTRICTED_CANCEL_AND_MODIFY).then((value) => {
        logger.out.info(
          `BfxBlotterOrdersContextMenuService: ORDER_UNRESTRICTED_CANCEL_AND_MODIFY ${value}`,
        );
        this.menuState.canUnrestrictedCancelModify = value;
      }),

      Promise.all([
        this.systemConfigService.hasRole(Roles.ORDER_UNRESTRICTED_CANCEL_AND_MODIFY),
        this.systemConfigService.hasRole(Roles.ORDER_CANCEL_CROSS_CLIENT),
        this.systemConfigService.hasRole(Roles.ORDER_CANCEL),
        this.systemConfigService.hasRole(Roles.ORDER_CANCEL_BM_HEDGE),
        this.systemConfigService.hasRole(Roles.DI_TRADER_TRANSFER),
      ]).then(
        ([canCancelUnrestricted, canCancelCross, canCancel, canCancelHedge, diTraderTransfer]) => {
          if (canCancelUnrestricted) {
            this.menuState.canUnrestrictedCancelModify = true;
            this.menuState.canCancelCrossClient = true;
            this.menuState.canCancel = true;
            this.menuState.canCancelHedge = true;
          }

          if (canCancelCross) {
            this.menuState.canCancelCrossClient = true;
            this.menuState.canCancel = true;
          }

          if (canCancel) {
            this.menuState.canCancel = true;
          }
          if (canCancelHedge) {
            this.menuState.canCancelHedge = true;
          }
          this.menuState.canTransferAccept = diTraderTransfer;
        },
      ),

      this.systemConfigService.hasRole(Roles.VIEW_ORDER_ENTRY).then((value) => {
        logger.out.info(`BfxBlotterOrdersContextMenuService: VIEW_ORDER_ENTRY  ${value}`);
        this.menuState.canViewOrderEntry = value;
      }),

      this.systemConfigService.hasRole(Roles.ORDER_ENTRY_UNRESTRICTED).then((value) => {
        logger.out.info(`BfxBlotterOrdersContextMenuService: ORDER_ENTRY_UNRESTRICTED  ${value}`);
        this.menuState.canEnterOrdersUnrestricted = value;
      }),

      this.systemConfigService.hasUnlock().then((value) => {
        logger.out.info(`BfxBlotterOrdersContextMenuService: hasUnlock ${value}`);
        this.menuState.canUnlock = value;
      }),

      this.systemConfigService.hasRole(Roles.ORDER_TRANSFER).then((value) => {
        logger.out.info(`BfxBlotterOrdersContextMenuService: ORDER_TRANSFER ${value}`);
        this.menuState.canTransfer = value;
      }),

      this.systemConfigService.hasRole(Roles.ORDER_TRANSFER_UNRESTRICTED).then((value) => {
        logger.out.info(`BfxBlotterOrdersContextMenuService: ORDER_TRANSFER_UNRESTRICTED ${value}`);
        this.menuState.canTransferUnrestricted = value;
      }),

      this.systemConfigService.hasRole(Roles.ORDER_STEAL_FROM_AUTO).then((value) => {
        logger.out.info(`BfxBlotterOrdersContextMenuService: ORDER_STEAL_FROM_AUTO ${value}`);
        this.menuState.canStealFromAuto = value;
      }),

      this.systemConfigService.hasRole(Roles.ORDER_EXTERNAL_TRANSFER_ABORT).then((value) => {
        logger.out.info(
          `BfxBlotterOrdersContextMenuService: ORDER_EXTERNAL_TRANSFER_ABORT ${value}`,
        );
        this.menuState.canAbortExternalTransfer = value;
      }),

      this.systemConfigService.hasRole(Roles.ORDER_EXTERNAL_FORCE_STEAL).then((value) => {
        logger.out.info(
          `BfxBlotterOrdersContextMenuService: ROLE_ORDER_EXTERNAL_FORCE_STEAL ${value}`,
        );
        this.menuState.canForceSteal = value;
      }),

      this.systemConfigService.hasRole(Roles.ORDER_STEAL_FROM_MANUAL).then((value) => {
        logger.out.info(`BfxBlotterOrdersContextMenuService: ORDER_STEAL_FROM_MANUAL ${value}`);
        this.menuState.canStealFromManual = value;
      }),

      this.systemConfigService.hasRole(Roles.DI_TRADER_ACCEPT).then((value) => {
        logger.out.info(`BfxBlotterOrdersContextMenuService: DI_TRADER_ACCEPT ${value}`);
        this.menuState.canAccept = value;
      }),

      this.systemConfigService.hasRole(Roles.DI_TRADER_REJECT).then((value) => {
        logger.out.info(`BfxBlotterOrdersContextMenuService: DI_TRADER_REJECT ${value}`);
        this.menuState.canReject = value;
      }),

      this.systemConfigService.hasRole(Roles.DI_TRADER_UNRESTRICTED).then((value) => {
        logger.out.info(`BfxBlotterOrdersContextMenuService: DI_TRADER_UNRESTRICTED ${value}`);
        this.menuState.canAcceptUnrestricted = value;
      }),

      this.systemConfigService.hasRole(Roles.ROLE_ORDER_PAUSE_RESUME).then((value) => {
        logger.out.info(`BfxBlotterOrdersContextMenuService: ROLE_ORDER_PAUSE_RESUME ${value}`);
        this.menuState.canPauseResume = value;
      }),

      this.systemConfigService
        .getSystemConfig(SysFeatures.featuresEnablePrivateComments)
        .then((value: string) => {
          logger.out.info(`BfxBlotterOrdersContextMenuService: EnablePrivateComments ${value}`);
          this.menuState.sysConfigCommentsPrivateEnabled = value === "Y" || value === "y";
        }),

      this.systemConfigService
        .getSystemConfig(SysFeatures.privatisePrivateComments)
        .then((value: string) => {
          logger.out.info(`BfxBlotterOrdersContextMenuService: EnablePrivateComments ${value}`);
          this.menuState.sysConfigCommentsPrivatePrivatized = value === "Y" || value === "y";
        }),
      this.systemConfigService
        .getSystemConfig(SysFeatures.privatiseClientComments)
        .then((value: string) => {
          logger.out.info(`BfxBlotterOrdersContextMenuService: EnablePrivateComments ${value}`);
          this.menuState.sysConfigCommentsClientPrivatized = value === "Y" || value === "y";
        }),

      this.systemConfigService.hasRole(Roles.DI_APPROVE_FILL).then((value) => {
        logger.out.info(`BfxBlotterOrdersContextMenuService: DI_APPROVE_FILL ${value}`);
        this.menuState.canDIApproveFill = value;
      }),

      this.systemConfigService.hasRole(Roles.DI_CANCEL_FILL).then((value) => {
        logger.out.info(`BfxBlotterOrdersContextMenuService: DI_CANCEL_FILL ${value}`);
        this.menuState.canDICancelFill = value;
      }),

      this.systemConfigService.hasRole(Roles.ROLE_ORDER_EXPORT_FOR_IMPORT).then((value) => {
        logger.out.info(
          `BfxBlotterOrdersContextMenuService: ROLE_ORDER_EXPORT_FOR_IMPORT ${value}`,
        );
        this.menuState.canExportForImport = value;
      }),

      this.systemConfigService.hasRole(Roles.ROLE_ORDER_ALGO_INFLIGHT).then((value) => {
        logger.out.info(`BfxBlotterOrdersContextMenuService: ROLE_ORDER_ALGO_INFLIGHT ${value}`);
        this.menuState.canChangeInflightStatus = value;
      }),

      this.systemConfigService.hasRole(Roles.ROLE_ORDER_EXPORT).then((value) => {
        logger.out.info(`BfxBlotterOrdersContextMenuService: ROLE_ORDER_EXPORT ${value}`);
        this.menuState.canExport = value;
      }),

      this.systemConfigService.hasRole(Roles.ROLE_ORDER_ALGO_FILL_NOW).then((value) => {
        logger.out.info(`BfxBlotterOrdersContextMenuService: ROLE_ORDER_ALGO_FILL_NOW ${value}`);
        this.menuState.canFillNow = value;
      }),

      this.activeUserService.getActiveUserData().then(
        (activeUser: ActiveUser) => {
          this.menuState.activeUser = activeUser;
        },
        (reason) => {
          logger.out.error(`BfxBlotterOrdersContextMenuService: ActiveUser ${reason}`);
        },
      ),

      this.traderGroupService.isPrimed().then(() => {
        this.traderGroupService.getAll().then((values: Array<CubiqTS.TradingGroupImpl>) => {
          values.forEach((value) => {
            if (value && value.groupId) {
              let sid = value.groupId.toString();

              this.menuState.traderGroups.set(value.groupId.toString(), value);
            }
          });
        });
      }),
    ];

    this.initialised = Promise.all(this.contextmenuPromises).then(
      () => {
        logger.out.info(`BfxBlotterOrdersContextMenuService: initMenuItems`);
        this.initMenuItems();
        return true;
      },
      (reason) => {
        logger.out.error(`ERROR: BfxBlotterOrdersContextMenuService.prime(): reason: ${reason}`);
        return false;
      },
    );
  }

  // TODO - remove
  protected getEnabledActions(
    items: Array<any>,
    selectedIds: Array<string>,
  ): Array<BfxBlotterAction> {
    return [];
  }

  // DON'T
  protected getActionDecorators(items: Array<any>): any {
    // nothing to add
  }

  protected prepareActions(ladderBlotter: any): void {
    // nothing to add
  }

  public getMenuItems(): Array<BfxBlotterContextMenuItem<CubiqTS.OrderDataImpl>> {
    if (!this.menuItems) {
      this.initMenuItems();
    }
    return this.menuItems;
  }

  public getActiveUser(): ActiveUser {
    return this.menuState.activeUser;
  }

  public getTraderGroups(): Array<CubiqTS.TradingGroupImpl> {
    // TODO check if in BM mode when supported

    return Array.from(this.menuState.traderGroups.values()).filter((tg) => {
      return (
        tg.groupId.toString() != "-1" &&
        this.menuState.activeUser.tradingGroupsIdSet.has(tg.groupId.toString()) &&
        !tg.isBenchmarkFg &&
        !tg.isExternallyFilledFg
      );
    });
  }

  /**
   * Private methods
   */

  protected initMenuItems(): void {}

  public fireActionEvent(
    actionId: BfxBlotterAction,
    ladderBlotter: any,
    selectedIds?: Array<string>,
    selectedItems?: Array<any>,
    itemState?: ContextMenuItemState,
    hide?: boolean,
    menuBoundingClientRect?: any,
    actionEvent?: any,
  ): void {
    let event: BfxBlotterActionEvent = new BfxBlotterActionEvent(
      actionId,
      ladderBlotter,
      this.getSelectedIds(selectedIds, ladderBlotter),
      this.getSelectedItems(selectedItems, ladderBlotter),
      itemState,
      hide,
      menuBoundingClientRect,
      actionEvent,
    );
    let selectedIdSet = new Set(event.selectedIds);

    // TODO bfx-ladder-orders .getSelected already does this?
    if (ladderBlotter.supportsAggregation && ladderBlotter.gridOptions.api) {
      let selectedNodes: Array<RowNode> = ladderBlotter.gridOptions.api.getSelectedNodes();
      let selectedNodeIds: Array<string> = selectedNodes
        .filter((node: RowNode) => node.data.aggregationOrdersId == null)
        .map((node: RowNode) => node.id);
      if (selectedNodes != null && selectedNodes.length > 0) {
        selectedNodes
          .filter((node: RowNode) => Array.isArray(node.data.aggregationOrdersId))
          .forEach((node: RowNode) => {
            let aggregateOrderIds: Array<string> = node.data.aggregationOrdersId;
            aggregateOrderIds
              .filter((orderId: string) => selectedNodeIds.indexOf(orderId) < 0)
              .forEach((orderId: string) => {
                let row: RowNode = ladderBlotter.gridOptions.api.getRowNode(orderId);
                if (
                  ladderBlotter.isExternalFilterVisible(row) &&
                  selectedIdSet.has(orderId) === false
                ) {
                  event.selectedIds.push(orderId);
                }
              });
          });
      }
    } // is this requried? -dc

    this.actionSubject.next(event);
  }

  public buildMenu(
    blotter: any,
    pageX: number,
    pageY: number,
    selectedIds: Array<string>,
    selectedItems?: Array<object> | null,
  ): void {
    if ((selectedIds && selectedIds.length > 0) || (selectedItems && selectedItems.length > 0)) {
      this.contextMenuService.getMenuItems(selectedIds, this.menuState).then((response: any) => {
        let menuItems = response.menuItems;
        menuItems = this.reduceVisibleMenuItems(menuItems, selectedIds, selectedItems);

        // TODO
        let actionDecorators: any = response.actionDecorators;
        let enabledActionIds = [];
        if (Array.isArray(menuItems)) {
          enabledActionIds = menuItems
            .filter((menuItem) => menuItem.isEnabled)
            .map((menuItem) => menuItem.actionId);
        }
        // for template's use
        this.menuItems = menuItems;

        this.showMenuSubject.next(
          new BfxLadderBlotterOrdersContextMenuEvent(
            BfxLadderBlotterContextMenuEventType.MENU_SHOW,
            blotter,
            pageX,
            pageY,
            enabledActionIds,
            actionDecorators,
            menuItems,
          ),
        );
      });
    }
  }
}
